/**
 * BillingContext — the ONE place `/billing/entitlement` is read, and the
 * single source of truth every billing-consuming component derives
 * visibility from.
 *
 * DARK SEMANTICS (F14 spec, standing rule): while `billing_enabled` is off,
 * EVERY /billing route 404s and no endpoint ever returns 402 — dark means
 * "literally nothing billing-related renders". There is deliberately NO
 * separate frontend env flag for this: `status` is derived PURELY from
 * whether `GET /billing/entitlement` 404s (billingApi.isBillingDark), which
 * is the task's explicit single-source-of-truth instruction. A component
 * that wants to know "should I render at all" reads `useBilling().enabled`
 * and nothing else.
 *
 * FOUR-STATE, not a boolean (the reason a bare boolean is unsafe): a naive
 * `enabled` that starts `false` and flips `true` only on a confirmed 200
 * cannot be told apart from "confirmed dark" by a ROUTE GUARD that must
 * decide whether to redirect away — it would bounce an enabled-billing user
 * who is simply still waiting on the first fetch. `status` distinguishes
 * `'loading'` (don't know yet — wait) from `'disabled'` (CONFIRMED dark, a
 * clean 404 — safe to redirect/hide) from `'error'` (a TRANSIENT failure —
 * network/5xx/timeout — we simply failed to ask; NOT the same as confirmed
 * dark, see below) from `'enabled'` (confirmed on). Plain UI components only
 * need the `enabled` convenience boolean (`status === 'enabled'`), which is
 * safely false during 'loading', 'disabled', AND 'error' — so "render null
 * unless we have affirmative proof billing is on" falls out for free and
 * nothing ever flashes premium UI prematurely. Route guards
 * (BillingGatedRoute.js) read `status` directly so they can wait out
 * 'loading', offer a RETRY on 'error', and only redirect on a CONFIRMED
 * 'disabled' (F14-PR3.2, review S4 — folding a transient failure into
 * 'disabled', as this used to do, meant a route guard would REDIRECT AWAY a
 * genuinely-enabled user on a single network blip; that is a strictly worse
 * failure mode than a route guard that occasionally shows a retry panel).
 *
 * 'disabled' IS ONLY EVER SET ON A CONFIRMED, CLEAN 404 (`isBillingDark`) —
 * that is a real signal (billing really is off), so it is the ONLY status
 * allowed to unconditionally override even a previously-'enabled' snapshot.
 * Every OTHER failure mode is TRANSIENT by definition (we don't actually
 * know whether billing is on or off, we just failed to ask), and is NEVER
 * allowed to downgrade a snapshot we already confirmed was 'enabled' — see
 * `load()`'s catch branch. Plain UI components (which only read `enabled`)
 * therefore never flicker off because of a momentary blip; only a route
 * guard, which reads `status` directly, needs to know the difference at all.
 *
 * `error` is exposed separately (non-null only on a genuine fetch failure,
 * never on a clean dark-404) so a surface that specifically wants to say "we
 * couldn't check your plan" (BillingGatedRoute's retry panel,
 * EntitlementStatusBlock) can, without every OTHER consumer needing to
 * handle a fourth state — and it is attached WITHOUT disturbing `status`
 * when a refresh fails after a confirmed-enabled snapshot, so a widget that
 * only reads `.enabled`/`.active`/etc. never even notices the failed
 * refresh happened.
 *
 * REQUEST-VERSION PROTECTION (F14-PR3.2, review S3/S4): `load()` can be
 * called concurrently from multiple call sites — the mount effect, a manual
 * `refresh()` (BillingGatedRoute's retry, CheckoutFlow's already-entitled
 * celebration), and ActivationPage's post-purchase refresh all race in
 * principle. A monotonic `requestSeqRef` tags each call; a response is only
 * APPLIED if no NEWER call has been issued since — an older, slower request
 * that resolves AFTER a newer one already landed is silently discarded
 * rather than overwriting fresher state with stale data. This is what makes
 * ActivationPage's "call refresh() the moment a purchase activates" safe:
 * without it, a slow initial mount fetch that happens to resolve just after
 * the post-purchase refresh could clobber the fresh `active:true` back to
 * whatever the entitlement looked like before the purchase.
 *
 * PRELOAD EVERYTHING: mounted once at the authenticated app root (App.js,
 * inside AuthenticatedApp — mirrors useEngagementBeacon's placement), so the
 * entitlement check is already warm by the time a student navigates into
 * Prep/Guardian/Settings, instead of every surface re-fetching it cold.
 *
 * The mount fetch (and refresh()) is tagged `x-background-request` —
 * mirrors triggerAnalyticsPrefetch/triggerLoginPrewarm's exact reasoning
 * (App.js): a stale-token race on an automatic, supplementary read must
 * never silently log the student out. Entitlement state is never the thing
 * that gates a session's validity.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getEntitlement, isBillingDark, friendlyError } from './billingApi';

const BillingContext = createContext(null);

const INITIAL = {
  status: 'loading',
  active: false,
  entitlementStatus: null,
  source: null,
  tier: null,
  currentPeriodEnd: null,
  expiresAt: null,
  freeAllowance: null,
  freeUsed: null,
  // `self_checkout` (F14-PR3.2): 'allowed' | 'guardian_required' |
  // 'age_unknown' | 'not_student', or `null` on an OLDER backend that
  // doesn't send the field yet. `null` is the SAFE fallback value on
  // purpose — every gate this drives (CheckoutFlow.js) is a strict string
  // match against one of the three known "block/redirect" values, so `null`
  // (and any future value this client doesn't recognize yet) falls through
  // to today's existing discover-then-refuse behavior rather than being
  // mistaken for a block.
  selfCheckout: null,
  error: null,
};

function fromPayload(data) {
  return {
    status: 'enabled',
    active: data?.active === true,
    entitlementStatus: data?.status ?? null,
    source: data?.source ?? null,
    tier: data?.tier ?? null,
    currentPeriodEnd: data?.current_period_end ?? null,
    expiresAt: data?.expires_at ?? null,
    freeAllowance:
      typeof data?.free_full_length_allowance === 'number'
        ? data.free_full_length_allowance
        : null,
    freeUsed:
      typeof data?.free_full_length_used === 'number'
        ? data.free_full_length_used
        : null,
    selfCheckout: typeof data?.self_checkout === 'string' ? data.self_checkout : null,
    error: null,
  };
}

export function BillingProvider({ children }) {
  const [state, setState] = useState(INITIAL);
  const unmountedRef = useRef(false);
  // Request-version protection (review S3/S4) — see the file header. Every
  // `load()` invocation claims the next sequence number; a response is only
  // applied if IT is still the most recent call issued by the time it
  // resolves, so an older, slower request can never clobber a newer one's
  // result (e.g. a slow mount fetch resolving after ActivationPage's
  // post-purchase refresh already landed the fresh `active:true`).
  const requestSeqRef = useRef(0);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const load = useCallback(async () => {
    const seq = (requestSeqRef.current += 1);
    const isStale = () => unmountedRef.current || requestSeqRef.current !== seq;
    try {
      const data = await getEntitlement({
        headers: { 'x-background-request': 'true' },
      });
      if (isStale()) return;
      setState(fromPayload(data));
    } catch (err) {
      if (isStale()) return;
      if (isBillingDark(err)) {
        // A CONFIRMED, clean 404 — billing really is off. This is a real
        // signal, not a blip, so unlike the transient branch below it DOES
        // override even a previously-'enabled' snapshot.
        setState({ ...INITIAL, status: 'disabled' });
        return;
      }
      // Everything else (network failure, our OWN 5xx, a timeout, a stale
      // token 401ing this background call) is TRANSIENT (review S4) — we
      // don't actually know whether billing is on or off, we just failed to
      // ask. NEVER let this downgrade a snapshot we already confirmed was
      // 'enabled': every widget reading `.enabled`/`.active`/etc keeps
      // working exactly as it did before this failed refresh, and `error`
      // is attached purely as non-blocking, informational state for a
      // surface that specifically wants to say "last refresh failed"
      // without any OTHER consumer's behavior changing. Only when there is
      // NO confirmed answer yet (still 'loading', or a prior attempt also
      // failed) does the failure get its own 'error' status, so a route
      // guard can offer a retry instead of either redirecting away (only
      // correct for a CONFIRMED dark) or spinning forever.
      const message = friendlyError(err, "We couldn't check your plan just now.");
      setState((prev) =>
        prev.status === 'enabled'
          ? { ...prev, error: message }
          : { ...INITIAL, status: 'error', error: message },
      );
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo(
    () => ({
      ...state,
      enabled: state.status === 'enabled',
      refresh: load,
    }),
    [state, load],
  );

  return (
    <BillingContext.Provider value={value}>{children}</BillingContext.Provider>
  );
}

/**
 * useBilling() — the hook every billing-consuming component reads.
 *
 * Returns `{ status, enabled, active, entitlementStatus, source, tier,
 * currentPeriodEnd, expiresAt, freeAllowance, freeUsed, selfCheckout, error,
 * refresh }`.
 *
 * Falls back to a SAFE, always-disabled/never-throwing default when no
 * `<BillingProvider>` is mounted above the caller (a test rendering a leaf
 * component in isolation, or a future logged-out surface) — this hook must
 * never be the reason a component crashes; "no provider" degrades exactly
 * like "confirmed dark".
 */
export function useBilling() {
  const ctx = useContext(BillingContext);
  if (ctx) return ctx;
  return {
    ...INITIAL,
    status: 'disabled',
    enabled: false,
    refresh: async () => {},
  };
}
